import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  doc,
  increment,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { auth, db } from "../firebase";
import "../App.css";

const machinePlans = [
  { price: 5, dailyProfit: 0.25 },
  { price: 10, dailyProfit: 0.5 },
  { price: 15, dailyProfit: 0.75 },
  { price: 20, dailyProfit: 1 },
  { price: 30, dailyProfit: 1.5 },
  { price: 50, dailyProfit: 2.5 },
  { price: 100, dailyProfit: 5 },
  { price: 200, dailyProfit: 10 },
  { price: 300, dailyProfit: 15 },
  { price: 500, dailyProfit: 25 },
  { price: 1000, dailyProfit: 50 },
];

const CLAIM_DURATION = 24 * 60 * 60 * 1000;

function BuyMachine() {
  const [currentUser, setCurrentUser] = useState(null);
  const [activeMachines, setActiveMachines] = useState([]);
  const [balance, setBalance] = useState(0);
  const [totalEarned, setTotalEarned] = useState(0);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [buying, setBuying] = useState(false);
  const [claiming, setClaiming] = useState(false);

  const dailyProfit = useMemo(() => {
    return activeMachines.reduce(
      (total, machine) => total + Number(machine.dailyProfit || 0),
      0
    );
  }, [activeMachines]);

  const readyMachines = useMemo(() => {
  return activeMachines.filter((machine) => {
    const machineLastClaimTime = Number(
      machine.lastClaimTime ||
        machine.purchasedAtMillis ||
        Date.now()
    );

    return (
      machine.status === "Active" &&
      currentTime - machineLastClaimTime >= CLAIM_DURATION
    );
  });
}, [activeMachines, currentTime]);

const readyProfit = useMemo(() => {
  return readyMachines.reduce(
    (total, machine) =>
      total + Number(machine.dailyProfit || 0),
    0
  );
}, [readyMachines]);

const claimReady = readyMachines.length > 0;

const remainingTime = useMemo(() => {
  if (activeMachines.length === 0) {
    return CLAIM_DURATION;
  }

  const remainingTimes = activeMachines
    .filter((machine) => machine.status === "Active")
    .map((machine) => {
      const machineLastClaimTime = Number(
        machine.lastClaimTime ||
          machine.purchasedAtMillis ||
          currentTime
      );

      return Math.max(
        CLAIM_DURATION -
          (currentTime - machineLastClaimTime),
        0
      );
    });

  return remainingTimes.length > 0
    ? Math.min(...remainingTimes)
    : CLAIM_DURATION;
}, [activeMachines, currentTime]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    let unsubscribeUser = null;
    let unsubscribeMachines = null;

    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (unsubscribeUser) {
        unsubscribeUser();
        unsubscribeUser = null;
      }

      if (unsubscribeMachines) {
        unsubscribeMachines();
        unsubscribeMachines = null;
      }

      if (!user) {
        setCurrentUser(null);
        setBalance(0);
        setTotalEarned(0);
        setActiveMachines([]);
        return;
      }

      setCurrentUser(user);

      const userRef = doc(db, "users", user.uid);

      unsubscribeUser = onSnapshot(
        userRef,
        (snapshot) => {
          if (snapshot.exists()) {
            const userData = snapshot.data();

            setBalance(Number(userData.balance || 0));
            setTotalEarned(Number(userData.totalProfit || 0));
          } else {
            setBalance(0);
            setTotalEarned(0);
          }
        },
        (error) => {
          console.error("User listener error:", error);
        }
      );

      const machinesQuery = query(
        collection(db, "machines"),
        where("uid", "==", user.uid)
      );

      unsubscribeMachines = onSnapshot(
        machinesQuery,
        (snapshot) => {
          const machines = snapshot.docs.map((machineDocument) => ({
            id: machineDocument.id,
            ...machineDocument.data(),
          }));

          setActiveMachines(machines);
        },
        (error) => {
          console.error("Machines listener error:", error);
        }
      );
    });

    return () => {
      unsubscribeAuth();

      if (unsubscribeUser) {
        unsubscribeUser();
      }

      if (unsubscribeMachines) {
        unsubscribeMachines();
      }
    };
  }, []);

  const formatRemainingTime = (milliseconds) => {
    const totalSeconds = Math.max(
      0,
      Math.floor(Number(milliseconds || 0) / 1000)
    );

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )}:${String(seconds).padStart(2, "0")}`;
  };

  const buyMachine = async (plan) => {
    if (!currentUser) {
      alert("Please login first.");
      return;
    }

    if (buying) {
      return;
    }

    const alreadyPurchased = activeMachines.some(
      (machine) => Number(machine.price) === Number(plan.price)
    );

    if (alreadyPurchased) {
      alert("This machine is already active.");
      return;
    }

    if (balance < plan.price) {
      alert("Insufficient Balance!");
      return;
    }

    setBuying(true);

    try {
      const userRef = doc(db, "users", currentUser.uid);
      const machineRef = doc(collection(db, "machines"));
      const purchaseTime = Date.now();

      await runTransaction(db, async (transaction) => {
        const userSnapshot = await transaction.get(userRef);

        if (!userSnapshot.exists()) {
          throw new Error("User account not found.");
        }

        const userData = userSnapshot.data();
        const liveBalance = Number(userData.balance || 0);

        if (liveBalance < Number(plan.price)) {
          throw new Error("Insufficient Balance!");
        }

        transaction.update(userRef, {
  balance:
    liveBalance -
    Number(plan.price) +
    Number(plan.dailyProfit),

  totalProfit: increment(
    Number(plan.dailyProfit)
  ),
});

        transaction.set(machineRef, {
          uid: currentUser.uid,
          userEmail: currentUser.email || "",
          price: Number(plan.price),
          dailyProfit: Number(plan.dailyProfit),
          status: "Active",
          purchasedAt: serverTimestamp(),
          purchasedAtMillis: purchaseTime,
          lastClaimTime: purchaseTime,
         totalEarned: Number(plan.dailyProfit),
        });
      });

      setCurrentTime(Date.now());
      alert(`Machine $${plan.price} Activated Successfully!`);
    } catch (error) {
      console.error("Buy machine error:", error);
      alert(error.message || "Failed to buy machine.");
    } finally {
      setBuying(false);
    }
  };

  const claimProfit = async () => {
    if (!currentUser) {
      alert("Please login first.");
      return;
    }

    if (activeMachines.length === 0) {
      alert("Please buy a machine first.");
      return;
    }

    if (!claimReady) {
      alert(
        `Profit can be claimed after ${formatRemainingTime(remainingTime)}.`
      );
      return;
    }

    if (claiming) {
      return;
    }

    setClaiming(true);

    try {
      const userRef = doc(db, "users", currentUser.uid);
      const claimTime = Date.now();

      await runTransaction(db, async (transaction) => {
        const userSnapshot = await transaction.get(userRef);

        if (!userSnapshot.exists()) {
          throw new Error("User account not found.");
        }

        const machineSnapshots = [];

        for (const machine of activeMachines) {
          const machineRef = doc(db, "machines", machine.id);
          const machineSnapshot = await transaction.get(machineRef);

          machineSnapshots.push({
            ref: machineRef,
            snapshot: machineSnapshot,
          });
        }

        let claimAmount = 0;

        for (const machineItem of machineSnapshots) {
          if (!machineItem.snapshot.exists()) {
            continue;
          }

          const machineData = machineItem.snapshot.data();

          if (machineData.status !== "Active") {
            continue;
          }

          const machineLastClaimTime = Number(
            machineData.lastClaimTime ||
              machineData.purchasedAtMillis ||
              claimTime
          );

          if (claimTime - machineLastClaimTime < CLAIM_DURATION) {
  continue;
}

          claimAmount += Number(machineData.dailyProfit || 0);
        }

        if (claimAmount <= 0) {
          throw new Error("No profit is available to claim.");
        }

        transaction.update(userRef, {
          balance: increment(claimAmount),
          totalProfit: increment(claimAmount),
        });

        for (const machineItem of machineSnapshots) {
          if (!machineItem.snapshot.exists()) {
            continue;
          }

          const machineData = machineItem.snapshot.data();

          if (machineData.status !== "Active") {
            continue;
          }
const machineLastClaimTime = Number(
  machineData.lastClaimTime ||
    machineData.purchasedAtMillis ||
    claimTime
);

if (claimTime - machineLastClaimTime < CLAIM_DURATION) {
  continue;
}
          transaction.update(machineItem.ref, {
            lastClaimTime: claimTime,
            totalEarned: increment(Number(machineData.dailyProfit || 0)),
            lastClaimedAt: serverTimestamp(),
          });
        }
      });

      setCurrentTime(Date.now());

      alert(`$${readyProfit.toFixed(2)} profit claimed successfully!`);
    } catch (error) {
      console.error("Claim profit error:", error);
      alert(error.message || "Failed to claim profit.");
    } finally {
      setClaiming(false);
    }
  };

  return (
    <div className="app machine-page">
      <header className="page-header">
        <Link to="/dashboard" className="back-button">
          ←
        </Link>

        <div>
          <h1>Buy Machine</h1>
          <p>Choose your earning machine</p>
        </div>
      </header>

      <section className="machine-summary-card">
        <div>
          <span>Active Machines</span>
          <strong>{activeMachines.length}</strong>
        </div>

        <div>
          <span>Daily Profit</span>
          <strong>${dailyProfit.toFixed(2)}</strong>
        </div>

        <div>
          <span>Total Earned</span>
          <strong>${totalEarned.toFixed(2)}</strong>
        </div>
      </section>

      <section className="machine-information-card">
        <div className="machine-information-icon">⚡</div>

        <div>
          <h2>5% Daily Profit</h2>
          <p>Purchase a machine and claim profit after every 24 hours.</p>
        </div>
      </section>

      <section className="machine-note-card">
        <h3>Profit Claim</h3>

        {activeMachines.length === 0 ? (
          <p>Buy a machine to start the claim timer.</p>
        ) : claimReady ? (
          <p>Your daily profit is ready to claim.</p>
        ) : (
          <p>
            Next claim available in:{" "}
            <strong>{formatRemainingTime(remainingTime)}</strong>
          </p>
        )}

        <button
          type="button"
          className="buy-machine-button"
          onClick={claimProfit}
          disabled={!claimReady || claiming}
        >
          {claiming
            ? "Claiming..."
            : claimReady
           ? `Claim $${readyProfit.toFixed(2)} Profit`
            : "Claim After 24 Hours"}
        </button>
      </section>

      <div className="machine-section-heading">
        <div>
          <h2>Available Machines</h2>
          <p>Select a plan to start earning</p>
        </div>

        <span>{machinePlans.length} Plans</span>
      </div>

      <section className="machine-plans-grid">
        {machinePlans.map((plan) => {
          const isActive = activeMachines.some(
            (machine) => Number(machine.price) === Number(plan.price)
          );

          return (
            <article className="machine-plan-card" key={plan.price}>
              <div className="machine-plan-top">
                <div className="machine-plan-icon">
                  <img src="/snaptube-ghost.png" alt="SnapTube" />
                </div>

                <div className="machine-plan-status">
                  {isActive ? "Active" : "Available"}
                </div>
              </div>

              <h3>${plan.price} Machine</h3>

              <div className="machine-price">
                <strong>${plan.price}</strong>
                <span>One-time purchase</span>
              </div>

              <div className="machine-plan-details">
                <div>
                  <span>Daily Profit</span>
                  <strong>5%</strong>
                </div>

                <div>
                  <span>Daily Income</span>
                  <strong>${plan.dailyProfit.toFixed(2)}</strong>
                </div>

                <div>
                  <span>Claim Time</span>
                  <strong>24 Hours</strong>
                </div>

                <div>
                  <span>Machine Status</span>
                  <strong>Lifetime</strong>
                </div>
              </div>

              <button
                type="button"
                className="buy-machine-button"
                disabled={isActive || buying}
                onClick={() => buyMachine(plan)}
              >
                {isActive
                  ? "Active"
                  : buying
                  ? "Please Wait..."
                  : "Buy Machine"}
              </button>
            </article>
          );
        })}
      </section>

      <section className="machine-note-card">
        <h3>Important Information</h3>

        <p>
          Your investment amount remains locked. Only generated profit can be
          withdrawn.
        </p>

        <p>Available Balance: ${balance.toFixed(2)}</p>
      </section>

      <nav className="bottom-nav">
       <Link to="/dashboard" className="nav-item">
          <span className="nav-icon">🏠</span>
          <span>Dashboard</span>
        </Link>

        <Link to="/deposit" className="nav-item">
          <span className="nav-icon">💰</span>
          <span>Deposit</span>
        </Link>

        <Link to="/machine" className="nav-item active">
          <span className="nav-icon">▶️</span>
          <span>Machine</span>
        </Link>

        <Link to="/refer" className="nav-item">
          <span className="nav-icon">👥</span>
          <span>Referral</span>
        </Link>

        <Link to="/profile" className="nav-item">
          <span className="nav-icon">👤</span>
          <span>Profile</span>
        </Link>
      </nav>
    </div>
  );
}

export default BuyMachine;